/* eslint-disable react/no-unescaped-entities */

import { useEffect, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router';
import { Button, Center, Column, CustomSpinner, Divider, Row, Text } from '~/commonComponents';
import { EnforcePostAJobFunnelImprovements } from '~/constants/ff.posthog';
import { useGetProfessionalCountsByOffice } from '~/domain/Professionals/usecases/useGetProfessionalCountsByOffice';
import { useProfessionOptions } from '~/hooks';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import { firePermPostJobWorkDetailsSubmitted } from '~/posthogs/events';
import { Colors } from '~/themes/colors';
import { NumberUtils } from '~/utils/NumberUtils';
import { pluralize } from '~/utils/String';
import {
  AutoSaveStatus,
  EarningBenefitForm,
  GeneralErrorMessage,
  InfoSection,
  JobDetailsFormMapper,
  useJobDetailsForm,
} from '../JobDetailsForm';
import { ScheduleForm } from '../JobDetailsForm/components/ScheduleInfo';
import {
  CreatePermanentJobPostingFormSection,
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageRoutes,
} from './Types';
import {
  useAutoSaveDraftJobPost,
  useAutoScrollToSection,
  useDraftJobPost,
  useSaveJobPost,
} from './hooks';

export const WorkDetailsPage = () => {
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();
  const { draft, isLoading } = useDraftJobPost(id);
  const isEnforcePostAJobFunnelImprovement = useFeatureFlag(EnforcePostAJobFunnelImprovements);

  const scheduleRef = useRef<HTMLDivElement>(null);
  const earningsAndBenefitsRef = useRef<HTMLDivElement>(null);

  const controller = useJobDetailsForm({
    initialValues: draft,
  });

  useEffect(() => {
    if (draft) {
      controller.reset(JobDetailsFormMapper.fromEntity(draft));
    }
  }, [draft, controller]);

  useAutoScrollToSection({
    sectionRefs: {
      [CreatePermanentJobPostingFormSection.Schedule]: scheduleRef,
      [CreatePermanentJobPostingFormSection.EarningsAndBenefits]: earningsAndBenefitsRef,
    },
    isReady: !isLoading && !!draft,
  });

  const { isPending, hasFormErrors, onSave } = useSaveJobPost(controller, id, {
    targetStep: CreatePermanentJobPostingSteps.JobDescription,
    triggers: isEnforcePostAJobFunnelImprovement
      ? [
          'benefits',
          'benefitsOther',
          'compensationType',
          'expectedDays',
          'minRate',
          'payType',
          'scheduleType',
        ]
      : [
          'benefits',
          'benefitsOther',
          'compensationType',
          'expectedDays',
          'maxRate',
          'minRate',
          'payType',
          'scheduleType',
        ],
    onTrackSuccess: (postingId, formData) => {
      firePermPostJobWorkDetailsSubmitted({
        postingId,
        professionType: formData.professionType,
      });
    },
  });
  const {
    isLoading: isAutoSaveLoading,
    isShowSavedMessage,
    lastSavedAt,
  } = useAutoSaveDraftJobPost(controller, id);

  const user = useUserSelector();
  const {
    countsByProfession,
    isLoading: isCountLoading,
    isError: isCountError,
  } = useGetProfessionalCountsByOffice(user?.id, {
    enable: isEnforcePostAJobFunnelImprovement,
    distance: 25,
  });

  const { findByCode } = useProfessionOptions();
  const professionTypeValue = controller.watch('professionType');
  const professionTypeLabel = findByCode(professionTypeValue)?.label ?? '';

  const totalCount = countsByProfession
    ? (countsByProfession[professionTypeValue as keyof typeof countsByProfession] ?? 0)
    : 0;

  const showCount =
    isEnforcePostAJobFunnelImprovement && !isCountLoading && !isCountError && totalCount > 0;

  if (isLoading) {
    return (
      <Center marginBlock={48}>
        <CustomSpinner isLoading size={24} />
      </Center>
    );
  }

  if (isEnforcePostAJobFunnelImprovement) {
    return (
      <>
        <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />
        <Column alignItems="center" gap={16}>
          <Text bold fontSize={32} color={Colors.neutral_600} textAlign="center">
            {showCount
              ? // eslint-disable-next-line max-len
                `${NumberUtils.applyThousandSeparator(totalCount)} ${pluralize(professionTypeLabel, totalCount)} in Your Area`
              : 'Dental Professionals Near You Are on GoTu'}
          </Text>
          <Text fontSize={16} color={Colors.neutral_500} textAlign="center">
            Just set your schedule and compensation and we'll get your job in front of them.
          </Text>
          {hasFormErrors && <GeneralErrorMessage />}
          <FormProvider {...controller}>
            <Column width="100%" maxWidth={800} alignSelf="center">
              <InfoSection
                ref={scheduleRef}
                title="Schedule & Pay"
                titleFontSize={24}
                titleColor="neutral_600"
              >
                <Column gap={24}>
                  <ScheduleForm control={controller.control} errors={controller.formState.errors} />
                  <Divider height={1} marginVertical={0} />
                  <EarningBenefitForm />
                </Column>
              </InfoSection>
            </Column>
          </FormProvider>
          <Row justifyContent="center" marginTop={24}>
            <Button
              text="Add Description"
              onClick={onSave}
              isLoading={isPending || isAutoSaveLoading}
            />
          </Row>
        </Column>
      </>
    );
  }

  return (
    <>
      <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />
      <Column width="60%" alignSelf="center">
        <Text bold fontSize={22} marginBottom={14} marginLeft={24}>
          Step 2: Work Details
        </Text>
        {hasFormErrors && <GeneralErrorMessage />}
        <FormProvider {...controller}>
          <Column gap={16}>
            <InfoSection ref={scheduleRef} icon="calendar-solid" title="Schedule">
              <ScheduleForm control={controller.control} errors={controller.formState.errors} />
            </InfoSection>
            <InfoSection
              ref={earningsAndBenefitsRef}
              icon="money-pay-solid"
              title="Earnings and Benefits"
            >
              <EarningBenefitForm />
            </InfoSection>
          </Column>
        </FormProvider>
        <Row justifyContent="center" marginTop={24}>
          <Button text="Next" onClick={onSave} isLoading={isPending || isAutoSaveLoading} />
        </Row>
      </Column>
    </>
  );
};
